import { DefaultTeamDialog } from '@/components/templates/DefaultTeamDialog';
import type { TemplateDefaultTeam } from '@/config/objects/templates';
import { useTemplateContext } from '@/pages/templates/useTemplateContext';
import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyTableState,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * Template "Teams" tab — manages the template's DEFAULT teams. These are not
 * live teams: whenever an event is instantiated from the template, each
 * default team is created in the event with a fresh join code.
 */
export function TemplateTeamsTab() {
  const { template, isLoading } = useTemplateContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<TemplateDefaultTeam | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TemplateDefaultTeam | null>(null);

  const templateId = template?.id;

  const { data, isLoading: teamsLoading } = useQuery<{
    success: boolean;
    data: TemplateDefaultTeam[];
  }>({
    queryKey: ['TemplateTeams', templateId],
    queryFn: () => apiClient.get(`/templates/${templateId}/teams`),
    enabled: !!templateId,
  });

  const teams = useMemo(
    () => [...(data?.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  );

  const deleteMutation = useMutation({
    mutationFn: async (teamId: string) =>
      apiClient.delete(`/templates/${templateId}/teams/${teamId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateTeams', templateId] });
      toast.success('Default team deleted');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to delete default team');
    },
  });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (isLoading || teamsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Template not found</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-6">
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b flex flex-row items-center justify-between">
          <div>
            <CardTitle>Default Teams</CardTitle>
            <CardDescription>
              Seeded into every event created from this template — each event
              gets these teams with fresh join codes.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowAddDialog(true)} size="sm" icon={Plus}>
              Add Team
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {teams.length === 0 ? (
            <EmptyTableState
              icon={<Users className="h-8 w-8" />}
              title="No default teams yet"
              description="Add teams here to seed every event created from this template."
              action={{
                label: 'Add First Team',
                onClick: () => setShowAddDialog(true),
                icon: Plus,
              }}
            />
          ) : (
            <ul className="divide-y">
              {teams.map((team) => {
                const TeamIcon = getTeamIcon(team.iconName);
                return (
                  <li key={team.id} className="flex items-center gap-3 px-4 py-2.5">
                    {/* Team avatar — photo overrides icon when set */}
                    {team.photoUrl ? (
                      <img
                        src={team.photoUrl}
                        alt={`${team.name} team photo`}
                        className="h-8 w-8 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: `${team.iconColor}20` }}
                      >
                        <TeamIcon
                          className="h-4 w-4"
                          style={{ color: team.iconColor || DEFAULT_TEAM_ICON_COLOR }}
                        />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {team.emoji && <span aria-hidden="true">{team.emoji}</span>}
                        <span className="truncate text-sm font-medium text-foreground">
                          {team.name}
                        </span>
                        {team.isStaff && (
                          <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-amber-900">
                            Staff
                          </span>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          aria-label={`Actions for ${team.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditTarget(team)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit Team
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(team)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {showAddDialog && (
        <DefaultTeamDialog
          templateId={template.id}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
        />
      )}

      {editTarget && (
        <DefaultTeamDialog
          templateId={template.id}
          team={editTarget}
          open={!!editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Default Team"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? Events already created from this template keep their teams; new events will no longer include it.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
